import { useRef, useEffect, DependencyList } from 'react';

import { track } from './segmentEmitter';

export interface TrackChangesHookOptions {
  /**
   * Name of the event to be sent to Segment
   */
  eventName: string;
  /**
   * Use this option to generate the payload to be sent with the Segment event.
   * The function passed to this option will receive the previous value of the
   * `trackedValues` option array, allowing you to derive the Segment event
   * payload based on the previous and current tracked values.
   */
  getEventProperties: (prevTrackedValues: any[]) => Record<string, unknown>;
  /**
   * An array of values that, when changed, will trigger a Segment event to be
   * dispatched.
   */
  trackedValues: any[];
  /**
   * By default, the `useTrackChanges` hook uses the value of the `trackedValues`
   * option as its dependency list. However, if the `trackedValues` array contains
   * values that are non-primitive and unstable, the `getDependencyList` option can
   * be used to transform the value of `trackedValues` into a stable dependency list.
   *
   * A non-primitive element in the `trackedValues` option array is considered
   * "unstable" if its reference changes on each render cycle. This can happen
   * if you're tracking an object or array that is recomputed on each render cycle.
   * When an element in the `trackedValues` option array is unstable, it can cause
   * this hook to dispatch Segment events more often than intended.
   *
   * For example:
   *
   * ```javascript
   * const MyComponent = () => {
   *   // myTrackedValue is unstable because we re-create the array on each render cycle
   *   const myTrackedValue = ['a', 'b', 'c'];
   *
   *   // This will send a Segment event on each render cycle since myTrackedValue is unstable
   *   useTrackChanges({ trackedValues: [myTrackedValue], ... });
   *   ...
   * }
   * ```
   *
   * In this example, the `getDependencyList` option can be used to convert the unstable
   * tracked value into a stable value:
   *
   * ```javascript
   * const MyComponent = () => {
   *   // myTrackedValue is unstable because we re-create the array on each render cycle
   *   const myTrackedValue = ['a', 'b', 'c'];
   *
   *   // This will send a Segment event on each render cycle since myTrackedValue is unstable
   *   useTrackChanges({
   *     trackedValues: [myTrackedValue],
   *
   *     // By joining the array here, we convert myTrackedValue into a stable primitive string
   *     getDependencyList: ([_myTrackedValue]) => [_myTrackedValue.join(',')]
   *     ...
   *   });
   *   ...
   * }
   * ```
   */
  getDependencyList?: (currentTrackedValues: any[]) => DependencyList;
}

/**
 * Sends an event to Segment whenever a value in the `trackedValues` option array changes.
 * The Segment event name and payload can be configured based on a state change.
 */
export const useTrackChanges = ({
  eventName,
  getEventProperties,
  trackedValues,
  getDependencyList = () => trackedValues,
}: TrackChangesHookOptions) => {
  const prevTrackedValuesRef = useRef<any[]>();

  return useEffect(() => {
    /**
     * The useEffect hook fires both when the component mounts and when the
     * values in the dependency list change. We only want to dispatch a
     * Segment event when the dependency list changes, and therefore want
     * to skip sending a Segment event when the component mounts. Therefore,
     * this ref is used to determine whether or not this hook has been invoked
     * on-mount already. If the ref is set, then we can safely assume that the
     * useEffect hook was triggered by a change in the dependency list and not
     * by the component mounting.
     */
    if (prevTrackedValuesRef.current) {
      track(eventName, getEventProperties(prevTrackedValuesRef.current));
    }
    prevTrackedValuesRef.current = trackedValues;
  }, getDependencyList(trackedValues));
};
